import React, { useState, useEffect } from "react";
import {
  collection,
  onSnapshot,
  doc,
  updateDoc,
  query,
  orderBy,
  Timestamp,
} from "firebase/firestore";
import { db } from "../services/firebase";
import { useAuth } from "../contexts/AuthContext";
import Layout from "../component/Layout";
import {
  CheckCircle,
  XCircle,
  Clock,
  Search,
  Eye,
  Phone,
  Mail,
  AlertCircle,
  FileText,
  Download,
} from "lucide-react";
import "./PlatformOnboardingRequests.css";

const PlatformOnboardingRequests = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [roleFilter, setRoleFilter] = useState("all");
  const [selectedReq, setSelectedReq] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [adminNote, setAdminNote] = useState("");
  const [feedbackMsg, setFeedbackMsg] = useState({ type: "", text: "" });

  useEffect(() => {
    setLoading(true);
    const q = query(
      collection(db, "onboardingRequests"),
      orderBy("createdAt", "desc")
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const list = [];
        snapshot.forEach((docSnap) => {
          list.push({ id: docSnap.id, ...docSnap.data() });
        });
        setRequests(list);
        setLoading(false);
      },
      (err) => {
        console.error("Failed to load onboarding requests:", err);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const showFeedback = (type, text) => {
    setFeedbackMsg({ type, text });
    setTimeout(() => setFeedbackMsg({ type: "", text: "" }), 4000);
  };

  const handleUpdateStatus = async (reqId, newStatus) => {
    try {
      setActionLoading(true);
      const reqRef = doc(db, "onboardingRequests", reqId);
      await updateDoc(reqRef, {
        status: newStatus,
        reviewedBy: user?.mobile || "SuperAdmin",
        reviewedAt: Timestamp.now(),
        adminNotes: adminNote.trim() ? adminNote.trim() : undefined,
      });

      showFeedback("success", `Application status updated to "${newStatus.toUpperCase()}".`);
      setAdminNote("");
      if (selectedReq?.id === reqId) {
        setSelectedReq((prev) => ({
          ...prev,
          status: newStatus,
          reviewedBy: user?.mobile || "SuperAdmin",
          reviewedAt: new Date(),
        }));
      }
    } catch (err) {
      console.error("Error updating status:", err);
      showFeedback("error", `Failed to update status: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  // Filtered requests
  const filteredRequests = requests.filter((req) => {
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      req.businessName?.toLowerCase().includes(q) ||
      req.contactPerson?.toLowerCase().includes(q) ||
      req.phone?.toLowerCase().includes(q) ||
      req.city?.toLowerCase().includes(q) ||
      req.trackingId?.toLowerCase().includes(q) ||
      req.gstin?.toLowerCase().includes(q);

    const matchStatus = statusFilter === "all" || req.status === statusFilter;
    const matchRole = roleFilter === "all" || req.role === roleFilter;

    return matchSearch && matchStatus && matchRole;
  });

  // Metrics
  const totalCount = requests.length;
  const pendingCount = requests.filter((r) => r.status === "pending" || !r.status).length;
  const inReviewCount = requests.filter((r) => r.status === "in_review").length;
  const approvedCount = requests.filter((r) => r.status === "approved").length;
  const rejectedCount = requests.filter((r) => r.status === "rejected").length;

  const exportToCSV = () => {
    if (filteredRequests.length === 0) return;
    const headers = [
      "Tracking ID",
      "Business Name",
      "Contact Person",
      "Phone",
      "Email",
      "City",
      "State",
      "Role",
      "GSTIN",
      "Segment",
      "Turnover",
      "Status",
      "Date",
    ];

    const rows = filteredRequests.map((r) => [
      r.trackingId || r.id,
      `"${r.businessName || ""}"`,
      `"${r.contactPerson || ""}"`,
      r.phone || "",
      r.email || "",
      `"${r.city || ""}"`,
      `"${r.state || ""}"`,
      r.role || "buyer",
      r.gstin || "",
      `"${r.categoryInterest || ""}"`,
      `"${r.estimatedTurnover || ""}"`,
      r.status || "pending",
      r.createdAt?.toDate ? r.createdAt.toDate().toLocaleDateString() : "",
    ]);

    const csvContent =
      "data:text/csv;charset=utf-8," +
      [headers.join(","), ...rows.map((e) => e.join(","))].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `JMI_Onboarding_Requests_${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Layout pageTitle="Platform Onboarding Requests">
      <div className="onboarding-admin-container">
        {/* Header & Subtitle */}
        <div className="onboarding-admin-header">
          <div>
            <h2 className="admin-page-title">Jeweller & Enterprise Access Requests</h2>
            <p className="admin-page-desc">
              Prospective buyers, manufacturers, and operators who submitted applications via the JMI landing page gateway.
            </p>
          </div>
          <button className="btn-export-csv" onClick={exportToCSV} disabled={filteredRequests.length === 0}>
            <Download size={16} /> Export Applications CSV
          </button>
        </div>

        {/* Feedback Alert */}
        {feedbackMsg.text && (
          <div className={`admin-feedback-alert ${feedbackMsg.type}`}>
            {feedbackMsg.type === "success" ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
            <span>{feedbackMsg.text}</span>
          </div>
        )}

        {/* Stats Metrics Cards */}
        <div className="onboarding-stats-grid">
          <div className="stat-card stat-total" onClick={() => setStatusFilter("all")}>
            <div className="stat-label">Total Applications</div>
            <div className="stat-value">{totalCount}</div>
            <div className="stat-hint">All-time inbound</div>
          </div>
          <div className="stat-card stat-pending" onClick={() => setStatusFilter("pending")}>
            <div className="stat-label">Pending Review</div>
            <div className="stat-value">{pendingCount}</div>
            <div className="stat-hint">Needs verification</div>
          </div>
          <div className="stat-card stat-inreview" onClick={() => setStatusFilter("in_review")}>
            <div className="stat-label">In Review / Contacted</div>
            <div className="stat-value">{inReviewCount}</div>
            <div className="stat-hint">In tele-check</div>
          </div>
          <div className="stat-card stat-approved" onClick={() => setStatusFilter("approved")}>
            <div className="stat-label">Approved & Verified</div>
            <div className="stat-value">{approvedCount}</div>
            <div className="stat-hint">Access granted</div>
          </div>
          <div className="stat-card stat-rejected" onClick={() => setStatusFilter("rejected")}>
            <div className="stat-label">Rejected / Duplicate</div>
            <div className="stat-value">{rejectedCount}</div>
            <div className="stat-hint">Declined</div>
          </div>
        </div>

        {/* Controls Bar */}
        <div className="onboarding-controls-bar">
          <div className="search-input-wrap">
            <Search size={18} className="search-icon" />
            <input
              type="text"
              placeholder="Search by business name, contact, phone, city, GSTIN, tracking ID..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="filters-group">
            <div className="filter-item">
              <label>Status:</label>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="all">All Statuses ({totalCount})</option>
                <option value="pending">Pending ({pendingCount})</option>
                <option value="in_review">In Review ({inReviewCount})</option>
                <option value="approved">Approved ({approvedCount})</option>
                <option value="rejected">Rejected ({rejectedCount})</option>
              </select>
            </div>

            <div className="filter-item">
              <label>Role:</label>
              <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
                <option value="all">All Roles</option>
                <option value="buyer">Buyers / Retailers</option>
                <option value="seller">Manufacturers / Sellers</option>
                <option value="operator">Regional Operators</option>
              </select>
            </div>
          </div>
        </div>

        {/* Requests Table */}
        <div className="onboarding-table-card">
          {loading ? (
            <div className="table-loading-state">
              <div className="spinner"></div>
              <span>Loading onboarding requests from Firestore...</span>
            </div>
          ) : filteredRequests.length === 0 ? (
            <div className="table-empty-state">
              <FileText size={48} />
              <h3>No matching onboarding requests</h3>
              <p>Try modifying your search or filter parameters.</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="onboarding-data-table">
                <thead>
                  <tr>
                    <th>Ref ID & Date</th>
                    <th>Enterprise / Business</th>
                    <th>Applicant & Contact</th>
                    <th>Role Requested</th>
                    <th>Location</th>
                    <th>Segment & Volume</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRequests.map((req) => {
                    const status = req.status || "pending";
                    const createdDate = req.createdAt?.toDate
                      ? req.createdAt.toDate().toLocaleDateString("en-IN", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })
                      : "—";

                    return (
                      <tr key={req.id} className={`status-row-${status}`}>
                        <td>
                          <div className="ref-cell">
                            <span className="ref-code">{req.trackingId || req.id.slice(0, 10)}</span>
                            <span className="ref-date">{createdDate}</span>
                          </div>
                        </td>
                        <td>
                          <div className="business-cell">
                            <strong className="biz-name">{req.businessName}</strong>
                            {req.tradeName && <span className="biz-trade">T/A: {req.tradeName}</span>}
                            {req.gstin && <span className="biz-gstin">GST: {req.gstin}</span>}
                          </div>
                        </td>
                        <td>
                          <div className="contact-cell">
                            <span className="contact-name">{req.contactPerson}</span>
                            <span className="contact-phone">
                              <Phone size={12} /> +91 {req.phone}
                            </span>
                            {req.email && (
                              <span className="contact-email">
                                <Mail size={12} /> {req.email}
                              </span>
                            )}
                          </div>
                        </td>
                        <td>
                          <span className={`role-badge role-${req.role || "buyer"}`}>
                            {(req.role || "buyer").toUpperCase()}
                          </span>
                        </td>
                        <td>
                          <div className="location-cell">
                            <span>{req.city}</span>
                            <span className="loc-state">{req.state}</span>
                          </div>
                        </td>
                        <td>
                          <div className="segment-cell">
                            <span className="seg-name">{req.categoryInterest || "Gold 22K"}</span>
                            <span className="seg-vol">{req.estimatedTurnover || "—"}</span>
                          </div>
                        </td>
                        <td>
                          <span className={`status-pill pill-${status}`}>
                            {status === "in_review" ? "IN REVIEW" : status.toUpperCase()}
                          </span>
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button
                              className="btn-action-view"
                              title="View Application Details"
                              onClick={() => setSelectedReq(req)}
                            >
                              <Eye size={15} /> Review
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Detailed Application Review Modal */}
        {selectedReq && (
          <div className="modal-backdrop" onClick={() => setSelectedReq(null)}>
            <div className="application-modal-card" onClick={(e) => e.stopPropagation()}>
              <div className="app-modal-header">
                <div>
                  <div className="modal-ref-pill">
                    REF: {selectedReq.trackingId || selectedReq.id}
                  </div>
                  <h3>{selectedReq.businessName}</h3>
                  <span className={`role-badge role-${selectedReq.role || "buyer"}`}>
                    {(selectedReq.role || "buyer").toUpperCase()} ONBOARDING APPLICATION
                  </span>
                </div>
                <button className="btn-close-modal" onClick={() => setSelectedReq(null)}>
                  ✕
                </button>
              </div>

              <div className="app-modal-body">
                <div className="details-grid-2">
                  <div className="details-box">
                    <h4>🏢 Enterprise & Legal Credentials</h4>
                    <div className="detail-row">
                      <span className="d-label">Legal Name:</span>
                      <span className="d-val">{selectedReq.businessName}</span>
                    </div>
                    {selectedReq.tradeName && (
                      <div className="detail-row">
                        <span className="d-label">Trade Name:</span>
                        <span className="d-val">{selectedReq.tradeName}</span>
                      </div>
                    )}
                    <div className="detail-row">
                      <span className="d-label">GSTIN:</span>
                      <span className="d-val">{selectedReq.gstin || "Not provided / Under review"}</span>
                    </div>
                    {selectedReq.pancard && (
                      <div className="detail-row">
                        <span className="d-label">PAN Card:</span>
                        <span className="d-val">{selectedReq.pancard}</span>
                      </div>
                    )}
                    <div className="detail-row">
                      <span className="d-label">Source Channel:</span>
                      <span className="d-val">{selectedReq.source || "JMI Landing Portal"}</span>
                    </div>
                  </div>

                  <div className="details-box">
                    <h4>👤 Contact Representative</h4>
                    <div className="detail-row">
                      <span className="d-label">Contact Person:</span>
                      <span className="d-val">{selectedReq.contactPerson}</span>
                    </div>
                    {selectedReq.designation && (
                      <div className="detail-row">
                        <span className="d-label">Designation:</span>
                        <span className="d-val">{selectedReq.designation}</span>
                      </div>
                    )}
                    <div className="detail-row">
                      <span className="d-label">Mobile Number:</span>
                      <span className="d-val">
                        <a href={`tel:+91${selectedReq.phone}`}>+91 {selectedReq.phone}</a>
                      </span>
                    </div>
                    <div className="detail-row">
                      <span className="d-label">Email:</span>
                      <span className="d-val">
                        <a href={`mailto:${selectedReq.email}`}>{selectedReq.email || "—"}</a>
                      </span>
                    </div>
                    <div className="detail-row">
                      <span className="d-label">Market Location:</span>
                      <span className="d-val">{selectedReq.city}, {selectedReq.state}</span>
                    </div>
                  </div>
                </div>

                <div className="details-box full-width">
                  <h4>💎 Trading Parameters & Sourcing Profile</h4>
                  <div className="details-grid-3">
                    <div>
                      <span className="d-label">Product Segment:</span>
                      <strong className="d-val block">{selectedReq.categoryInterest || "Gold 22K (916)"}</strong>
                    </div>
                    <div>
                      <span className="d-label">Estimated Monthly Volume:</span>
                      <strong className="d-val block">{selectedReq.estimatedTurnover || "₹50 Lakhs - ₹2 Crore"}</strong>
                    </div>
                    <div>
                      <span className="d-label">Submission Date:</span>
                      <span className="d-val block">
                        {selectedReq.createdAt?.toDate ? selectedReq.createdAt.toDate().toLocaleString() : "—"}
                      </span>
                    </div>
                  </div>
                  {selectedReq.notes && (
                    <div className="applicant-notes-box">
                      <span className="d-label">Applicant Notes / Requirements:</span>
                      <p>{selectedReq.notes}</p>
                    </div>
                  )}
                </div>

                {/* Status & Review Actions */}
                <div className="admin-action-section">
                  <h4>⚙️ SuperAdmin Onboarding Verification Action</h4>
                  <div className="current-status-banner">
                    <span>Current Status:</span>
                    <strong className={`status-pill pill-${selectedReq.status || "pending"}`}>
                      {(selectedReq.status || "pending").toUpperCase()}
                    </strong>
                    {selectedReq.reviewedBy && (
                      <span className="reviewed-by-text">
                        (Reviewed by {selectedReq.reviewedBy})
                      </span>
                    )}
                  </div>

                  <div className="admin-notes-field">
                    <label>Admin Verification Remarks / Internal Audit Log:</label>
                    <textarea
                      rows="2"
                      placeholder="e.g. GST verified active on portal. Called owner Rajesh, onboarding approved for Buyer App..."
                      value={adminNote}
                      onChange={(e) => setAdminNote(e.target.value)}
                    />
                  </div>

                  <div className="action-button-row">
                    <button
                      className="btn-modal-action approve"
                      disabled={actionLoading || selectedReq.status === "approved"}
                      onClick={() => handleUpdateStatus(selectedReq.id, "approved")}
                    >
                      <CheckCircle size={16} /> Approve & Grant Verified Access
                    </button>

                    <button
                      className="btn-modal-action inreview"
                      disabled={actionLoading || selectedReq.status === "in_review"}
                      onClick={() => handleUpdateStatus(selectedReq.id, "in_review")}
                    >
                      <Clock size={16} /> Mark as In Verification / Contacted
                    </button>

                    <button
                      className="btn-modal-action reject"
                      disabled={actionLoading || selectedReq.status === "rejected"}
                      onClick={() => handleUpdateStatus(selectedReq.id, "rejected")}
                    >
                      <XCircle size={16} /> Reject Application
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default PlatformOnboardingRequests;
